import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AUTH_GRAVITEE_ENABLED } from '@/lib/constants';
import { ApiError } from '@/lib/api/api-error';
import { getApiErrorMessage } from '@/lib/api/client';
import { postData, putData } from '@/lib/api/client-v2';
import { notify } from '@/lib/notify';
import { queryKeys } from '@/lib/query-keys';
import { ENDPOINTS } from '@/types/api/endpoints';
import type {
    CommunicationPreferencesBody,
    DematBody,
    RegulatoryInfosBody,
    UpdatePasswordBody,
} from '@/types/api/profile';
import type { ChangeInformationsNaissanceValues } from '@/schemas/change-informations-naissance';

import { useCurrentUser } from '@/hooks/api/use-user';

/*
 * Écritures de l'écran « Mon compte » : mot de passe, informations de naissance, préférences de
 * communication et dématérialisation, sur l'API v2 perso. Reprises des middlewares redux
 * `customer/updatePassword`, `customer/updateBirthInfos` et `parameters`.
 */

export interface ProfileMutationVariables<TForm> {
    form: TForm;
    /** Appelée à la confirmation de la popin de succès (ou au succès, pour les préférences). */
    successFunction?: () => void;
}

const errorPopin = (message: string) =>
    notify({
        html: '<p>Une erreur est survenue : ' + message + '</p>',
        icon: 'error',
        title: 'Attention !',
        confirmButtonText: "J'ai compris",
    });

/** Message de la v1 pour un mot de passe actuel erroné (`passwords.wrong`, 406). */
const MAUVAIS_MOT_DE_PASSE = 'Mauvais mot de passe.';

/**
 * La v2 refuse un mot de passe actuel erroné en 422 sur `old_password` ; la v1 le disait en 406
 * « Mauvais mot de passe. ». On garde le texte de la v1.
 */
const passwordErrorMessage = (error: unknown): string =>
    error instanceof ApiError && error.fieldErrors['old_password'] !== undefined
        ? MAUVAIS_MOT_DE_PASSE
        : getApiErrorMessage(error);

/** Changement de mot de passe (`POST /me/password`). */
export const useUpdatePassword = () =>
    useMutation({
        mutationFn: ({ form }: ProfileMutationVariables<UpdatePasswordBody>) =>
            postData<null>(ENDPOINTS.mePassword, form),
        onSuccess: (_data, { successFunction }) => {
            void notify({
                // En MFA la session est fermée par le changement lui-même : on l'annonce.
                html: AUTH_GRAVITEE_ENABLED
                    ? '<div><p>Pour des raisons de sécurité, vous allez être invité à vous reconnecter avec votre nouveau mot de passe.</p></div>'
                    : '<div><p>Votre demande a bien été prise en compte.</p></div>',
                type: 'success',
                title: AUTH_GRAVITEE_ENABLED ? 'Mot de passe modifié' : 'Félicitations !',
                confirmButtonText: AUTH_GRAVITEE_ENABLED ? 'Me reconnecter' : "J'ai compris",
                successFunction,
            } as Parameters<typeof notify>[0]);
        },
        onError: (error) => {
            void errorPopin(passwordErrorMessage(error));
        },
    });

/** Formulaire v1 (`pays`, `ville`, `nom`…) → corps v2 (`birth_country_code`, `birth_city`, `birth_name`…). */
const toRegulatoryInfosBody = (form: ChangeInformationsNaissanceValues): RegulatoryInfosBody => ({
    birth_country_code: form.pays,
    birth_city: form.ville,
    birth_name: form.nom,
    statut_juridique: form.statut_juridique,
    siren_siret: form.siren_siret,
});

/**
 * Informations de naissance / société du tiers (`PUT /tiers/{id}/regulatory-infos`).
 *
 * Au succès, la liste des contrats est vidée pour être relue (elle porte le souscripteur).
 */
export const useUpdateBirthInfos = () => {
    const queryClient = useQueryClient();
    const tiersId = useCurrentUser().belair_id;

    return useMutation({
        mutationFn: ({ form }: ProfileMutationVariables<ChangeInformationsNaissanceValues>) =>
            putData<null>(ENDPOINTS.tiersRegulatoryInfos(String(tiersId)), toRegulatoryInfosBody(form)),
        onSuccess: (_data, { successFunction }) => {
            void queryClient.resetQueries({ queryKey: queryKeys.contracts, exact: true });
            void notify({
                html: '<p>Vos informations seront mises à jour sous 24h</p>',
                icon: 'success',
                title: 'Félicitations !',
                confirmButtonText: "J'ai compris",
                allowOutsideClick: false,
                allowEscapeKey: false,
                successFunction,
            });
        },
        onError: (error) => {
            void errorPopin(getApiErrorMessage(error));
        },
    });
};

/** Préférences de communication (`POST /tiers/{id}/communication-preferences`). Pas de popin au succès. */
export const useUpdateCommunicationPreferences = () => {
    const tiersId = useCurrentUser().belair_id;

    return useMutation({
        mutationFn: ({ form }: ProfileMutationVariables<CommunicationPreferencesBody>) =>
            postData<null>(ENDPOINTS.tiersCommunicationPreferences(String(tiersId)), form),
        onSuccess: (_data, { successFunction }) => {
            successFunction?.();
        },
        onError: (error) => {
            void errorPopin(getApiErrorMessage(error));
        },
    });
};

/**
 * Statut de dématérialisation de l'avis d'échéance (`POST /tiers/{id}/demat`).
 *
 * L'écran raisonne en `b_stopemail` (« F » = démat active) ; la v2 attend `active`.
 */
export const useUpdateDematStatut = () => {
    const tiersId = useCurrentUser().belair_id;

    return useMutation({
        mutationFn: ({ form }: ProfileMutationVariables<{ b_stopemail: 'T' | 'F' }>) =>
            postData<null>(ENDPOINTS.tiersDemat(String(tiersId)), { active: form.b_stopemail === 'F' } satisfies DematBody),
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: '<div><p>Votre demande de modification a bien été prise en compte</p></div>',
                icon: 'success',
                title: 'Félicitations !',
                confirmButtonText: "J'ai compris",
                successFunction,
            });
        },
        onError: (error) => {
            void errorPopin(getApiErrorMessage(error));
        },
    });
};
